import { Channel } from '@nextforce/api'
import { t } from 'app/i18l'
import { getClientEnvironment, getClientPlatform } from 'app/server/platform'
import moment from 'moment'
import { useOnboarding } from 'onboarding/service/context'
import { VerifyScreenProps } from 'onboarding/stack/types'
import VerifyView from 'onboarding/views/verify'
import React, { useCallback, useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { StatusBoundary } from 'shared/components/status_boundary'
import { handleWebLink } from 'shared/utils'
import { WebScreenParams } from 'web/types'
import { useQRCode } from 'web_login/queries/get_qrcode'
import {
  EXPIRATION_SECONDS as QR_CODE_EXPIRATION_SECONDS,
  useWebQRLogin,
} from 'web_login/queries/get_web_qr_login'

export function VerifyScreen({ navigation }: VerifyScreenProps) {
  const isWeb = Platform.OS === 'web'
  const [isLoading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()
  const [phoneNumber, setPhoneNumber] = useState<string>()
  const [channel, setChannel] = useState<Channel>('sms')
  const onboarding = useOnboarding()
  const qrCodeQuery = useQRCode({
    platformType: 'WEB',
  })
  const { data: qrCodeUri, dataUpdatedAt, deviceToken, refetch } = qrCodeQuery

  useWebQRLogin(isWeb ? deviceToken : undefined)

  const [seconds, setSeconds] = useState(QR_CODE_EXPIRATION_SECONDS)
  const [isQrExpired, setQrExpired] = useState(false)
  const [lastQrRefetchAt, setLastQrRefetchAt] = useState(0)

  const updateTimeSinceLastQueryRefresh = useCallback(() => {
    if (!dataUpdatedAt) {
      setSeconds(QR_CODE_EXPIRATION_SECONDS)
      return
    }

    const remaining =
      QR_CODE_EXPIRATION_SECONDS -
      moment().diff(moment(dataUpdatedAt), 'seconds')
    setSeconds(Math.max(remaining, 0))
  }, [dataUpdatedAt])

  useEffect(() => {
    if (!isWeb) {
      return
    }

    updateTimeSinceLastQueryRefresh()

    const intervalId = setInterval(() => {
      updateTimeSinceLastQueryRefresh()
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [dataUpdatedAt, isWeb, updateTimeSinceLastQueryRefresh])

  useEffect(() => {
    if (!isWeb) {
      return
    }

    if (!dataUpdatedAt) {
      return
    }

    if (Date.now() - lastQrRefetchAt < 5000) {
      return
    }

    if (seconds > 0 || isQrExpired) {
      return
    }

    setQrExpired(true)
    setLastQrRefetchAt(Date.now())
    refetch()
      .catch(() => null)
      .finally(() => setQrExpired(false))
  }, [dataUpdatedAt, isQrExpired, isWeb, lastQrRefetchAt, refetch, seconds])

  const onSubmit = useCallback(async () => {
    setLoading(true)

    try {
      const verificationData = await onboarding.verify(phoneNumber!, channel, {
        platform: getClientPlatform(),
        env: getClientEnvironment(),
      })

      setErrorMessage(undefined)
      setLoading(false)

      if (verificationData.verified) {
        navigation.navigate('activate', { phoneNumber: phoneNumber! })
      } else if (isWeb) {
        setErrorMessage(t('webOnboardingNotSupported'))
      } else {
        navigation.navigate('onboard', {
          phoneNumber: phoneNumber!,
          channel: channel,
        })
      }
    } catch (err: any) {
      setErrorMessage(err.message)
      setLoading(false)
    }
  }, [channel, isWeb, navigation, onboarding, phoneNumber])

  const onLinkPress = useCallback(
    (webParams: WebScreenParams) => handleWebLink(navigation, webParams),
    [navigation]
  )

  return (
    <StatusBoundary
      isLoading={isLoading}
      loadingMessage={t('verifying')}
      error={undefined}
    >
      <VerifyView
        {...{
          errorMessage,
          phoneNumber,
          channel,
          setPhoneNumber,
          setChannel,
          onLinkPress,
          qrCodeUri: isWeb ? qrCodeUri : undefined,
          isQrExpired: isWeb ? isQrExpired : false,
          timerSeconds: seconds,
          onSubmit,
        }}
      />
    </StatusBoundary>
  )
}
